import { TILE_SIZE } from "../models/common";
import { LootBox } from "../models/loot-box";
import { playSound } from "../utils/sound-utils";

/**
 * Interactable system interface
 */
export interface Interactable {
  type: string;
  x: number;
  y: number;
  tileIndex: number;
  roomName?: string;
  name?: string; // Optional name for named interactables (e.g. crafting stations)
  outline?: Phaser.GameObjects.Graphics;
}

/**
 * LootboxManager handles all highlighting logic for loot boxes and interactables.
 * This centralizes state management to prevent sync issues between scene updates.
 */
export class InteractionManager {
  // Current highlighted state
  private currentHighlightedLootBox: LootBox | null = null;
  private currentHighlightedInteractable: Interactable | null = null;

  // Previous highlighted state for change detection
  private previousHighlightedLootBox: LootBox | null = null;
  private previousHighlightedInteractable: Interactable | null = null;

  // Track if highlighting changed in this update cycle
  private lootBoxChanged: boolean = false;
  private interactableChanged: boolean = false;

  /**
   * Update highlighting based on player position and available objects.
   * Optimized: Only updates graphics when highlighting state changes.
   */
  public updateHighlights(
    scene: Phaser.Scene,
    playerTileX: number,
    playerTileY: number,
    player: any,
    interactables: Interactable[],
    lootBoxes: LootBox[],
  ): void {
    // Reset change flags
    this.lootBoxChanged = false;
    this.interactableChanged = false;

    // Check interactables first (higher priority)
    let highlightedInteractable: Interactable | null = null;

    if (interactables && interactables.length > 0) {
      // Find closest interactable within range
      for (const interactable of interactables) {
        const dist =
          Math.abs(playerTileX - interactable.x) +
          Math.abs(playerTileY - interactable.y);

        if (dist <= 1 && interactable.outline) {
          highlightedInteractable = interactable;
          break;
        }
      }

      // Only update outlines if interactable changed
      if (highlightedInteractable !== this.previousHighlightedInteractable) {
        // Hide previous interactable outline
        if (this.previousHighlightedInteractable?.outline) {
          this.previousHighlightedInteractable.outline.clear();
          this.previousHighlightedInteractable.outline.setVisible(false);
        }
        // Show new interactable outline
        if (highlightedInteractable?.outline) {
          highlightedInteractable.outline.clear();
          highlightedInteractable.outline.lineStyle(3, 0xffffff, 1);
          highlightedInteractable.outline.strokeRect(
            highlightedInteractable.x * TILE_SIZE,
            highlightedInteractable.y * TILE_SIZE,
            TILE_SIZE,
            TILE_SIZE,
          );
          highlightedInteractable.outline.setVisible(true);
        }
      }
    } else {
      // No interactables available, hide previous if any
      if (this.previousHighlightedInteractable?.outline) {
        this.previousHighlightedInteractable.outline.clear();
        this.previousHighlightedInteractable.outline.setVisible(false);
      }
    }

    // Update interactable highlighting
    this.setHighlightedInteractable(highlightedInteractable, scene);

    // Check loot boxes (only if no interactable is highlighted)
    let highlightedLootBox: LootBox | null = null;

    if (!highlightedInteractable) {
      // Locked chests are not interactable: exclude them entirely so they get
      // no outline and no OPEN/PICK UP button, same as if they weren't there.
      const availableLootBoxes = lootBoxes
        ? lootBoxes.filter((lb) => !lb.locked)
        : lootBoxes;

      if (availableLootBoxes && availableLootBoxes.length > 0) {
        // First, check if there's a loot box on the same tile as the player (highest priority)
        for (let i = 0; i < availableLootBoxes.length; i++) {
          const lootBox: LootBox = availableLootBoxes[i];
          if (lootBox.isPlayerOnSameTile(playerTileX, playerTileY)) {
            highlightedLootBox = lootBox as LootBox;
            break;
          }
        }

        // If no loot box on same tile and player has a movement direction, check movement direction
        if (!highlightedLootBox && player && player.lastMoveDirection) {
          const dir = player.lastMoveDirection;
          const targetTileX = playerTileX + (dir.x || 0);
          const targetTileY = playerTileY + (dir.y || 0);

          for (let i = 0; i < availableLootBoxes.length; i++) {
            const lootBox: LootBox = availableLootBoxes[i];
            if (
              lootBox.info.x === targetTileX &&
              lootBox.info.y === targetTileY &&
              lootBox.isPlayerAdjacent(playerTileX, playerTileY)
            ) {
              highlightedLootBox = lootBox as LootBox;
              break;
            }
          }
        }

        // If still no loot box highlighted, check for adjacent loot boxes (lowest priority)
        if (!highlightedLootBox) {
          let closestDistance = Infinity;
          for (let i = 0; i < availableLootBoxes.length; i++) {
            const lootBox: LootBox = availableLootBoxes[i];
            const dx = Math.abs(lootBox.info.x - playerTileX);
            const dy = Math.abs(lootBox.info.y - playerTileY);
            const distance = dx + dy;
            if (distance === 1 && distance < closestDistance) {
              closestDistance = distance;
              highlightedLootBox = lootBox as LootBox;
            }
          }
        }

        // Only update outlines if loot box changed
        if (highlightedLootBox !== this.previousHighlightedLootBox) {
          // Hide previous loot box outline
          if (this.previousHighlightedLootBox) {
            this.previousHighlightedLootBox.hideOutline();
          }
          // Show new loot box outline
          if (highlightedLootBox) {
            highlightedLootBox.renderOutline(scene);
          }
        }
      } else if (lootBoxes && Array.isArray(lootBoxes)) {
        // No loot boxes to highlight, hide previous if any
        if (this.previousHighlightedLootBox) {
          this.previousHighlightedLootBox.hideOutline();
        }
      }
    } else {
      // Interactable is highlighted, hide previous loot box outline if any
      if (this.previousHighlightedLootBox) {
        this.previousHighlightedLootBox.hideOutline();
      }
    }

    // Update loot box highlighting
    this.setHighlightedLootBox(highlightedLootBox, scene);
  }

  /**
   * Set the currently highlighted loot box and detect changes
   */
  private setHighlightedLootBox(
    lootBox: LootBox | null,
    scene: Phaser.Scene,
  ): void {
    // Store previous state before updating
    this.previousHighlightedLootBox = this.currentHighlightedLootBox;
    this.currentHighlightedLootBox = lootBox;

    // Detect change by comparing references and positions
    this.lootBoxChanged =
      lootBox !== this.previousHighlightedLootBox ||
      !!(
        lootBox &&
        this.previousHighlightedLootBox &&
        (lootBox.info.x !== this.previousHighlightedLootBox.info.x ||
          lootBox.info.y !== this.previousHighlightedLootBox.info.y)
      );

    // Play sound when loot box becomes highlighted
    if (lootBox && lootBox !== this.previousHighlightedLootBox) {
      playSound(scene, "highlight");
    }
  }

  /**
   * Set the currently highlighted interactable and detect changes
   */
  private setHighlightedInteractable(
    interactable: Interactable | null,
    scene: Phaser.Scene,
  ): void {
    // Store previous state before updating
    this.previousHighlightedInteractable = this.currentHighlightedInteractable;
    this.currentHighlightedInteractable = interactable;

    // Detect change
    this.interactableChanged =
      interactable !== this.previousHighlightedInteractable;

    // Play sound when interactable becomes highlighted
    if (interactable && interactable !== this.previousHighlightedInteractable) {
      playSound(scene, "highlight");
    }
  }

  /**
   * Get the currently highlighted loot box
   */
  public getHighlightedLootBox(): LootBox | null {
    return this.currentHighlightedLootBox;
  }

  /**
   * Get the previously highlighted loot box
   */
  public getPreviousHighlightedLootBox(): LootBox | null {
    return this.previousHighlightedLootBox;
  }

  /**
   * Get the currently highlighted interactable
   */
  public getHighlightedInteractable(): Interactable | null {
    return this.currentHighlightedInteractable;
  }

  /**
   * Get the previously highlighted interactable
   */
  public getPreviousHighlightedInteractable(): Interactable | null {
    return this.previousHighlightedInteractable;
  }

  /**
   * Check if the loot box highlight changed since last update
   */
  public hasLootBoxChanged(): boolean {
    return this.lootBoxChanged;
  }

  /**
   * Check if the interactable highlight changed since last update
   */
  public hasInteractableChanged(): boolean {
    return this.interactableChanged;
  }

  /**
   * Reset all highlighting state and hide any outline currently on screen
   * (e.g. when the player dies while standing next to a highlighted object).
   */
  public reset(): void {
    this.currentHighlightedLootBox?.hideOutline();
    if (this.currentHighlightedInteractable?.outline) {
      this.currentHighlightedInteractable.outline.setVisible(false);
    }
    this.currentHighlightedLootBox = null;
    this.currentHighlightedInteractable = null;
    this.previousHighlightedLootBox = null;
    this.previousHighlightedInteractable = null;
    this.lootBoxChanged = false;
    this.interactableChanged = false;
  }

  /**
   * Clear a specific loot box if it matches the highlighted one
   * (useful when loot boxes are removed from the world)
   */
  public clearLootBoxIfMatches(x: number, y: number): void {
    if (
      this.currentHighlightedLootBox &&
      this.currentHighlightedLootBox.info.x === x &&
      this.currentHighlightedLootBox.info.y === y
    ) {
      this.currentHighlightedLootBox = null;
    }
  }
}
